import { Component, effect, input, output, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { AdminButtonComponent, AdminIconComponent } from '../../shared/admin';
import { AdminCrmGlobalSearchComponent } from '../../shared/admin/admin-crm-global-search.component';
import { AdminNotificationBellComponent } from '../../shared/admin/admin-notification-bell.component';

export type AdminTopbarUser = {
  name: string;
  roleKey: string;
  avatarUrl?: string | null;
};

@Component({
  selector: 'app-admin-topbar',
  standalone: true,
  imports: [RouterLink, AdminButtonComponent, AdminIconComponent, AdminNotificationBellComponent, AdminCrmGlobalSearchComponent],
  template: `
    <header class="cg-admin-navbar">
      <div class="cg-admin-navbar__left">
        <app-admin-button variant="ghost" type="button" (click)="menuToggle.emit()">
          <span class="cg-admin-navbar__menu-icon cg-admin-navbar__menu-icon--mobile">
            <app-admin-icon name="menu" />
          </span>
          <span class="cg-admin-navbar__menu-icon cg-admin-navbar__menu-icon--desktop">
            @if (sidebarCollapsed()) {
              <app-admin-icon name="panel-left-open" />
            } @else {
              <app-admin-icon name="panel-left-close" />
            }
          </span>
        </app-admin-button>
        <p class="cg-admin-navbar__title">{{ pageTitle() }}</p>
      </div>

      <div class="cg-admin-navbar__center">
        <app-admin-crm-global-search />
      </div>

      <a routerLink="/" class="cg-btn cg-btn-ghost cg-admin-navbar__marketing-link">
        <app-admin-icon name="home" [size]="16" />
        <span>Back to Marketing</span>
      </a>

      <div class="cg-admin-navbar__actions">
        <app-admin-notification-bell />
        @if (user(); as u) {
          <div class="cg-admin-navbar__profile">
            <div class="cg-admin-navbar__avatar-slot">
              <span class="cg-admin-navbar__avatar cg-admin-navbar__avatar--initials" aria-hidden="true">
                {{ initials(u.name) }}
              </span>
              @if (showAvatarImg(u)) {
                <img
                  [src]="u.avatarUrl!"
                  alt=""
                  class="cg-admin-navbar__avatar cg-admin-navbar__avatar--img"
                  (error)="avatarLoadFailed.set(true)"
                />
              }
            </div>
            <div class="cg-admin-navbar__profile-text">
              <div class="cg-admin-navbar__user-name">{{ u.name }}</div>
              <div class="cg-admin-navbar__user-role">{{ u.roleKey }}</div>
            </div>
          </div>
        }
        <app-admin-button variant="secondary" type="button" (click)="logout.emit()">
          <app-admin-icon name="log-out" [size]="16" />
          Logout
        </app-admin-button>
      </div>
    </header>
  `,
})
export class AdminTopbarComponent {
  readonly pageTitle = input('Platform');
  readonly user = input<AdminTopbarUser | null>(null);
  readonly sidebarCollapsed = input(false);
  readonly menuToggle = output<void>();
  readonly logout = output<void>();

  readonly avatarLoadFailed = signal(false);

  constructor() {
    effect(() => {
      this.user();
      this.avatarLoadFailed.set(false);
    });
  }

  showAvatarImg(u: AdminTopbarUser): boolean {
    if (this.avatarLoadFailed()) return false;
    const url = u.avatarUrl?.trim();
    return !!url && /^https?:\/\//i.test(url);
  }

  initials(name: string): string {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return `${parts[0][0] ?? ''}${parts[1][0] ?? ''}`.toUpperCase();
    }
    const single = parts[0] ?? '?';
    return single.slice(0, 2).toUpperCase();
  }
}
